/*
  Grouped cart lines in the cart drawer (Oct 2026, AG1-style).

  snippets/cart-drawer.liquid renders lines that share a variant and a selling
  plan (e.g. two one-off bags split into two qty-1 lines by the "25% off second
  bag" discount) as ONE row with its own stepper. This file wires that stepper.
  All rendering is server-side Liquid; this only issues the cart calls and then
  re-renders the drawer the same way assets/cart-drawer-upsell.js does.

  Event delegation on document, so it survives drawer re-renders (scripts
  injected by a re-render never execute). Form-encoded bodies only: Shopify
  ignores selling_plan on JSON bodies, and JSON gets mangled through the dev
  proxy used for testing.

    PLUS   POST /cart/add.js     id=<variant>&quantity=1[&selling_plan=<id>]
    MINUS  POST /cart/change.js  id=<last key>&quantity=<qty - 1, or 0>
    TRASH  POST /cart/change.js  id=<key>&quantity=0, for every group line in
           turn (next key read from the previous response; see removeAll)

  The stock Dawn stepper on single-line rows is untouched (cart.js).
*/
(function () {
  if (window.kultraCartGroupWired) return;
  window.kultraCartGroupWired = true;

  var ACTION_SELECTOR = '[data-kgroup-action]';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function encode(data) {
    return Object.keys(data)
      .map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(data[k]); })
      .join('&');
  }

  function post(path, data) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: encode(data),
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok) {
          var err = new Error(path + ' ' + res.status);
          err.body = json;
          throw err;
        }
        return json;
      });
    });
  }

  function split(value) {
    return (value || '').split('||').filter(function (part) { return part !== ''; });
  }

  // Same refresh as cart-drawer-upsell.js: re-fetch the drawer section and
  // swap #CartDrawer in place, then the header cart count.
  function refresh() {
    return Promise.all([
      fetch(root() + '?section_id=cart-drawer').then(function (r) { return r.text(); }),
      fetch(root() + '?section_id=cart-icon-bubble').then(function (r) { return r.text(); }).catch(function () { return null; }),
    ]).then(function (parts) {
      var freshDoc = new DOMParser().parseFromString(parts[0], 'text/html');
      var fresh = freshDoc.getElementById('CartDrawer');
      var current = document.getElementById('CartDrawer');
      if (fresh && current) current.innerHTML = fresh.innerHTML;

      if (parts[1]) {
        var bubbleSection = new DOMParser().parseFromString(parts[1], 'text/html').querySelector('.shopify-section');
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubbleSection && bubble) bubble.innerHTML = bubbleSection.innerHTML;
      }

      var drawer = document.querySelector('cart-drawer');
      if (drawer) {
        var freshDrawer = freshDoc.querySelector('cart-drawer');
        var empty = freshDrawer ? freshDrawer.classList.contains('is-empty') : false;
        drawer.classList.toggle('is-empty', empty);
        var overlay = document.getElementById('CartDrawer-Overlay');
        if (overlay && typeof drawer.close === 'function') {
          overlay.addEventListener('click', drawer.close.bind(drawer));
        }
        if (typeof drawer.open === 'function') drawer.open();
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-group' });
      }
    });
  }

  function plus(row) {
    var data = { id: row.getAttribute('data-kgroup-variant'), quantity: 1 };
    var plan = row.getAttribute('data-kgroup-plan');
    if (plan) data.selling_plan = plan;
    return post('cart/add.js', data);
  }

  // Lines of this row's group in a cart JSON (same variant + same selling
  // plan, or both one-off), in cart order.
  function groupLines(row, cart) {
    var variant = row.getAttribute('data-kgroup-variant');
    var plan = row.getAttribute('data-kgroup-plan') || '';
    return ((cart && cart.items) || []).filter(function (item) {
      var itemPlan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? String(item.selling_plan_allocation.selling_plan.id)
        : '';
      return String(item.variant_id) === variant && itemPlan === plan;
    });
  }

  function fetchCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  // Line keys are NOT stable: the second-bag discount re-splits the bag lines
  // on every change, so the surviving line comes back under a new key
  // (verified on the dev store). Keys rendered into the row are used first;
  // if one has gone stale (400), the current lines are read from /cart.js.
  function changeLast(row, key, qty) {
    return post('cart/change.js', { id: key, quantity: qty > 1 ? qty - 1 : 0 }).catch(function (e) {
      if (!e || !/ 4\d\d$/.test(e.message)) throw e;
      return fetchCart().then(function (cart) {
        var lines = groupLines(row, cart);
        if (!lines.length) return cart;
        var line = lines[lines.length - 1];
        return post('cart/change.js', { id: line.key, quantity: line.quantity > 1 ? line.quantity - 1 : 0 });
      });
    });
  }

  function minus(row) {
    var keys = split(row.getAttribute('data-kgroup-keys'));
    var qtys = split(row.getAttribute('data-kgroup-qtys'));
    if (!keys.length) return Promise.resolve();
    var last = keys.length - 1;
    return changeLast(row, keys[last], parseInt(qtys[last], 10) || 1);
  }

  // Sets every line of the group to 0, one /cart/change.js at a time. The
  // first call uses the first rendered key; each following call takes the
  // next group line's CURRENT key from the cart JSON the previous call
  // returned, since removing one split line re-keys the others.
  function removeAll(row) {
    var keys = split(row.getAttribute('data-kgroup-keys'));
    if (!keys.length) return Promise.resolve();
    var guard = 0;
    function zero(key) {
      return post('cart/change.js', { id: key, quantity: 0 })
        .catch(function (e) {
          if (!e || !/ 4\d\d$/.test(e.message)) throw e;
          return fetchCart().then(function (cart) {
            var lines = groupLines(row, cart);
            return lines.length && lines[0].key !== key ? post('cart/change.js', { id: lines[0].key, quantity: 0 }) : cart;
          });
        })
        .then(function (cart) {
          var next = groupLines(row, cart)[0];
          guard += 1;
          if (next && guard < 20) return zero(next.key);
          return cart;
        });
    }
    return zero(keys[0]);
  }

  function setLoading(row, on) {
    row.classList.toggle('is-loading', on);
    var spinner = row.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
    row.querySelectorAll(ACTION_SELECTOR).forEach(function (button) {
      if (on) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    });
  }

  function showError(row, message) {
    var text = row.querySelector('.kgroup-error .cart-item__error-text');
    if (text) text.textContent = message || '';
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest(ACTION_SELECTOR) : null;
    if (!button) return;
    var row = button.closest('[data-kgroup]');
    if (!row) return;
    event.preventDefault();
    if (button.disabled || row.classList.contains('is-loading')) return;

    var action = button.getAttribute('data-kgroup-action');
    var run = action === 'plus' ? plus : action === 'minus' ? minus : action === 'remove' ? removeAll : null;
    if (!run) return;

    setLoading(row, true);
    showError(row, '');
    run(row)
      .then(refresh)
      .catch(function (e) {
        console.error('[cart-group] ' + action + ' failed', e);
        var message = e && e.body && (e.body.description || e.body.message);
        // Re-render anyway so the drawer shows the real cart state; surface
        // Shopify's message (e.g. out of stock) on the fresh row if possible.
        return refresh().then(function () {
          if (!message) return;
          var freshRow = document.querySelector('[data-kgroup][data-kgroup-variant="' + row.getAttribute('data-kgroup-variant') + '"]');
          if (freshRow) showError(freshRow, message);
        });
      })
      .catch(function (e) {
        console.error('[cart-group] refresh failed', e);
        setLoading(row, false);
      });
  });
})();
